import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import type { RootState } from "app/store";
import type {
  CallContact,
  PracticeCall,
  PracticeCallDetail
} from "services/dialstack";

// One websocket `call.missed` frame (dialstack/notifications.py, ENG-2476):
// an inbound call just ended with nobody on it. The backend sends it to every
// voice-enabled staffer of the practice the moment the call ends (and again
// when the provider's final record corrects a false answer), so the browser
// can banner the missed call and the Calls workspace can add the row without
// a reload. Voicemail-status calls arrive too — the workspace lists them under
// Missed — and the banner leaves those to the voicemail alert.
export type MissedCallFrame = {
  // Backend Call.id: what the workspace fetches and keys its rows by.
  callId: number;
  // Provider call id (Call.dialstack_call_id).
  providerCallId: string | null;
  clinicId: number | null;
  status: string | null;
  fromNumber: string;
  fromName: string | null;
  // Who the backend says called: patient, contact, caller ID or the number
  // formatted for reading — the same label the mobile push shows.
  callerLabel: string;
  patientIds: number[];
  contact: CallContact | null;
  endedAt: string | null;
  receivedAt: number;
};

type MissedCallsState = {
  // Newest first, bounded: consumers act on what arrived since they mounted.
  frames: MissedCallFrame[];
};

const MAX_FRAMES = 20;

const initialState: MissedCallsState = { frames: [] };

// The auth logout thunk's fulfilled action type; matched by string so this
// slice does not import the (import-heavy) auth slice.
const LOGOUT_FULFILLED = "auth/logout/fulfilled";

export const missedCallsSlice = createSlice({
  name: "missedCalls",
  initialState,
  reducers: {
    // Upsert by call id: the correction frame for a call replaces the first
    // one, and a re-delivered frame stays one entry.
    missedCallReceived: (state, action: PayloadAction<MissedCallFrame>) => {
      state.frames = [
        action.payload,
        ...state.frames.filter(
          (frame) => frame.callId !== action.payload.callId
        )
      ].slice(0, MAX_FRAMES);
    },
    missedCallsCleared: (state) => {
      state.frames = [];
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type === LOGOUT_FULFILLED,
      (state) => {
        state.frames = [];
      }
    );
  }
});

export const { missedCallReceived, missedCallsCleared } =
  missedCallsSlice.actions;

export const selectMissedCallFrames = (state: RootState) =>
  state.missedCalls.frames;

// The list row for a call the workspace fetched after its frame. The detail
// payload is a superset of the list row except for `user`, which it nests.
export const practiceCallRowFromDetail = (
  detail: PracticeCallDetail
): PracticeCall => {
  const { transcript, voicemail, staff_note_by, user, ...row } = detail;
  return {
    ...row,
    user: typeof user === "object" && user !== null ? user.id : user
  };
};

// Whether a row the server sent already reflects the call's end the frame
// announced. Rows carry no updated_at; the end time and the answer are what
// a call.end (or its correction) changes, so a server row matching both is at
// least as fresh as the fetched one and keeps anything that landed later
// (insight, tags).
const reflectsSameEnd = (server: PracticeCall, fetched: PracticeCall) =>
  server.ended_at === fetched.ended_at &&
  server.answered_at === fetched.answered_at;

const startedAtMs = (call: PracticeCall) =>
  call.started_at ? Date.parse(call.started_at) : Number.NEGATIVE_INFINITY;

// Merge the rows fetched after a `call.missed` frame over the loaded list on
// read, the way the attachment overlay does (call-attachments.slice.ts):
// the paginated list accumulates rows in local usePagination state and is
// never invalidated (the page-0 refetch footgun, SN-18212). A loaded row for
// the same call — a call that was ringing when the page loaded, or a stale
// answered row the provider later corrected — is replaced; a call the list
// has not seen is inserted in newest-first order, but only while the list is
// unfiltered (`insertMissing`): a filtered or searched list cannot tell
// client-side whether the call belongs.
export const withEndedCallRows = (
  rows: PracticeCall[],
  ended: Record<number, PracticeCall>,
  insertMissing: boolean
): PracticeCall[] => {
  const endedIds = Object.keys(ended).map(Number);
  if (endedIds.length === 0) return rows;
  const loadedIds = new Set(rows.map((row) => row.id));
  const merged = rows.map((row) => {
    const fetched = ended[row.id];
    return fetched && !reflectsSameEnd(row, fetched) ? fetched : row;
  });
  if (!insertMissing) return merged;
  const missing = endedIds
    .filter((id) => !loadedIds.has(id))
    .map((id) => ended[id])
    .sort((a, b) => startedAtMs(b) - startedAtMs(a));
  for (const call of missing) {
    const at = merged.findIndex((row) => startedAtMs(row) < startedAtMs(call));
    merged.splice(at === -1 ? merged.length : at, 0, call);
  }
  return merged;
};

export default missedCallsSlice.reducer;
